
<template>
  <div :class="loginBgUrl ? 'min-h-screen bg-cover bg-center bg-no-repeat' : ''" :style="loginBgStyle">
    <Navbar hide-action :class="loginBgUrl ? 'bg-white/90 backdrop-blur' : ''" />

    <div class="max-w-md mx-auto mt-16 bg-white p-8 rounded-xl shadow-md">
      <h2 class="text-2xl font-bold text-center mb-2 text-gray-800">Welcome Back</h2>
      <p class="text-sm text-center text-gray-500 mb-6">Sign in with your email or matric number</p>

      <Message v-if="reasonMessage" severity="info" :closable="false" class="mb-4" data-testid="login-reason">
        {{ reasonMessage }}
      </Message>

      <form class="flex flex-col gap-4" @submit.prevent="login">
        <!-- Identifier (Email or Matric Number) -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Email or Matric Number</label>
          <InputText
            v-model="identifier"
            placeholder="e.g. admin@school.com or MATRIC NUMBER"
            class="w-full"
            data-testid="login-identifier"
            @input="clearError('identifier')"
          />
          <small v-if="getErrorMessage('identifier')" class="text-red-600">
            {{ getErrorMessage('identifier') }}
          </small>
        </div>

        <!-- Password -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Password</label>
          <Password
            v-model="password"
            placeholder="Enter Password"
            toggleMask
            inputClass="w-full"
            class="w-full"
            :feedback="false"
            data-testid="login-password"
            @input="clearError('password')"
          />
          <small v-if="getErrorMessage('password')" class="text-red-600">
            {{ getErrorMessage('password') }}
          </small>
        </div>

        <div class="text-right">
          <RouterLink to="/forgot-password" class="text-primary text-sm hover:underline">
            Forgot Password?
          </RouterLink>
        </div>

        <!-- Login Button -->
        <Button
          :label="isSubmitting ? 'Logging in...' : 'Login'"
          icon="pi pi-sign-in"
          type="submit"
          class="bg-primary hover:bg-primary/90 border-none text-white w-full"
          :loading="isSubmitting"
          data-testid="login-btn"
        />
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useToast } from 'primevue/usetoast';
import Navbar from '@/components/Auth/Navbar.vue';
import { useUserStore } from '@/store/userStore';
import { studentLandingRoute } from '@/utils/studentPortalMode';
import { unifiedLoginMutation } from '@/graphql';
import { useFormErrors } from '@/composables/useFormErrors';
import { useBranding } from '@/composables/useBranding';
import { SessionStructure, TermStructure } from '@/constants/session-structure.constant';
import { useSchoolMutations } from '@/composables/mutations/school/useSchoolMutations';
import { loginReasonMessage, postLoginTarget, withStaffRedirect } from '@/utils/authRedirect';
import { userHomeRoute } from '@/utils/portalAccess';

const router = useRouter();
const route = useRoute();
// Why the user is here (session expired / signed out) and where to return to.
const reasonMessage = computed(() => loginReasonMessage(route.query.reason));
const toast = useToast();
const userStore = useUserStore();
const { serverErrors, handleServerErrors, clearErrors: clearServerErrors, getErrorMessage } = useFormErrors();
const { createSessionStructure, createTermsStructure } = useSchoolMutations();
const { loginBgUrl } = useBranding();

const loginBgStyle = computed(() =>
  loginBgUrl.value ? { backgroundImage: `url('${loginBgUrl.value}')` } : undefined,
);

const identifier = ref('');
const password = ref('');
const isSubmitting = ref(false);

const { mutate: loginMutate } = unifiedLoginMutation();

const clearError = (field: string) => {
  delete serverErrors.value[field];
};

const login = async() => {
  clearServerErrors();

  // Client-side validation
  if (!identifier.value.trim()) {
    serverErrors.value.identifier = ['Email or matric number is required'];
  }
  if (!password.value) {
    serverErrors.value.password = ['Password is required'];
  }
  if (Object.keys(serverErrors.value).length) return;

  isSubmitting.value = true;

  try {
    const result = await loginMutate({
      identifier: identifier.value.trim(),
      password: password.value,
    });

    const response = result?.data?.login;

    if (!response) {
      throw new Error('Invalid response from server');
    }

    if (response.status === 'error' || !response.token) {
      serverErrors.value.password = [response.message || 'Invalid credentials'];
      toast.add({
        severity: 'error',
        summary: 'Login Failed',
        detail: response.message || 'Invalid credentials',
        life: 3000,
      });
      return;
    }

    const { token, user } = response;

    if (!token || !user) {
      throw new Error('Invalid login response');
    }

    // Save to store
    userStore.login(user, token);

    toast.add({
      severity: 'success',
      summary: 'Login Successful',
      detail: 'Welcome!',
      life: 3000,
    });

    // Per-school portals (staff / guardian) until the me-query reports them.
    if (response.loginSchools) {
      userStore.setLoginSchoolPortals(response.loginSchools);
    }

    // Route based on user type
    if (user.__typename === 'Student') {
      await userStore.getCurrentUser();
      const returnTo = postLoginTarget(route.query.redirect, 'Student');
      // CBT-only schools land straight on the exam surface (#326); the router
      // guard still bounces a returnTo outside that surface.
      await router.push(returnTo ?? { name: studentLandingRoute(userStore.isCbtOnlyStudentPortal) });
    } else {
      // User (admin/staff/guardian) — check if multi-school
      const schools = (response.schools ?? []).filter((s): s is NonNullable<typeof s> => !!s);
      const totalSchools = response.school_count ?? schools.length;

      if (totalSchools > 1) {
        // Multi-school user — the picker pages through the list itself.
        // Keep what the login sent only when it is the whole list: for a
        // large membership it is a single arbitrary school, and storing
        // that would pass it off as everything the user belongs to.
        if (schools.length === totalSchools) {
          userStore.setUserSchools(schools);
        }
        await router.push(withStaffRedirect('/select-school', route.query.redirect));
      } else {
        // Auto-select the single school (if any)
        if (schools.length === 1) {
          userStore.selectSchool(schools[0]!);
        }
        await userStore.getCurrentUser();
        await router.push(postLoginTarget(route.query.redirect, 'User') ?? { name: userHomeRoute(userStore.isGuardianOnly) });
        // First-time school setup is an admin concern; a guardian-only account has none.
        if (!userStore.isGuardianOnly) {
          await setupSchoolStructure();
        }
      }
    }
  } catch (error) {
    handleServerErrors(error);

    if (Object.keys(serverErrors.value).length === 0) {
      toast.add({
        severity: 'error',
        summary: 'Login Failed',
        detail: 'An unexpected error occurred. Please try again.',
        life: 3000,
      });
      serverErrors.value.password = ['Login failed'];
    }
  } finally {
    isSubmitting.value = false;
  }
};

/**
 * Handle session/term structure setup for admin users (first-time school setup).
 */
async function setupSchoolStructure() {
  type SchoolType = 'primary' | 'secondary' | 'tertiary'
  const schoolType = userStore.tempSchoolType?.toLowerCase() as SchoolType | undefined;

  if (schoolType && SessionStructure[schoolType]) {
    const sessionStructure = SessionStructure[schoolType];
    await createSessionStructure({
      name: sessionStructure.name,
      description: sessionStructure.description,
      is_default: sessionStructure.is_default,
    });
  }

  if (schoolType && TermStructure[schoolType]) {
    const termStructure = TermStructure[schoolType];
    const sessionStructureId = userStore.tempSessionStructureId ?? '';
    for (const term of termStructure) {
      await createTermsStructure({
        session_structure_id: sessionStructureId || '',
        name: term.name,
        sequence: term.sequence,
      });
    }
  }
}
</script>

<style scoped>
.p-inputtext:focus {
  border-color: #047857 !important;
  box-shadow: 0 0 0 1px #047857 !important;
}
</style>
